import { normalizeId } from './ids';
import { slugify, uniqueKey } from '../components/items/multiAddEditorModel';
import { formatCurrencyValue, formatMeasurementValue } from './formatters';
import { isCurrencyValue } from './currency';
import { isMeasurementValue } from './units';

/**
 * The pure half of the card action editor (KPR-550): a stored `card-action`
 * automation (keepr-api docs/SCHEMA.md § Card actions) as the editor's draft,
 * the draft as the payload the API takes, the words for a refusal and for a
 * row of the list.
 *
 * The API validates the whole action at write time, with sentences written for
 * a person, so nothing here re-implements its rules: it builds the documented
 * JSON, leaves every empty optional out, and says where a refusal belongs.
 * What it must NOT do is lose data on a no-op save, so a step this editor
 * does not build (an `update-items`, a key a newer API adds) rides through as
 * stored.
 */

export const MAX_INPUTS = 20;
export const MAX_STEPS = 20;
export const MAX_LABEL = 60;
export const MAX_CONFIRM = 300;
export const MAX_REASON = 120;

export const SET_VALUES = 'set-elements';
export const CREATE_ITEM = 'create-item';
/** A stored step this editor does not build: kept as it is, shown in words. */
export const KEPT = 'kept';

export const STEP_LABELS = {
	[SET_VALUES]: 'Set values on this item',
	[CREATE_ITEM]: 'Create a related item',
	[KEPT]: 'Another step',
};

let seq = 0;
export const nextKey = (prefix = 'k') => `${prefix}${++seq}`;

// ---------------------------------------------------------------- value rows

export const emptyRow = () => ({ key: nextKey('r'), name: '', value: '' });

/** A stored values map as rows. A list stays a list: a lookup's records are one. */
export const rowsFromObject = obj => Object.entries(obj || {}).map(([name, value]) => ({
	key: nextKey('r'),
	name,
	value: value === null || value === undefined ? '' : (Array.isArray(value) ? value : `${value}`),
}));

export const rowsToObject = rows => {
	const out = {};
	(rows || []).forEach(row => {
		const name = String(row?.name || '').trim();
		if (name) out[name] = row?.value ?? '';
	});
	return out;
};

const omitKeys = (obj, keys) => Object.fromEntries(Object.entries(obj || {}).filter(([k]) => !keys.includes(k)));

// The keys this editor owns per step type; the rest of a stored step is carried through.
const OWNED = {
	[SET_VALUES]: ['type', 'elements'],
	[CREATE_ITEM]: ['type', 'card_id', 'elements', 'linkBackElement', 'dedupe'],
};

// --------------------------------------------------------------------- steps

export function stepFromAction(action) {
	const key = nextKey('s');
	if (action?.type === SET_VALUES) return { key, type: SET_VALUES, raw: action, rows: rowsFromObject(action.elements) };
	if (action?.type === CREATE_ITEM) {
		return {
			key,
			type: CREATE_ITEM,
			raw: action,
			cardId: normalizeId(action.card_id) || '',
			rows: rowsFromObject(action.elements),
			linkBackElement: action.linkBackElement || '',
			dedupeOn: Array.isArray(action.dedupe?.on) ? action.dedupe.on : [],
		};
	}
	return { key, type: KEPT, raw: action };
}

export function newStep(type) {
	if (type === CREATE_ITEM) return { key: nextKey('s'), type, cardId: '', rows: [emptyRow()], linkBackElement: '', dedupeOn: [] };
	return { key: nextKey('s'), type: SET_VALUES, rows: [emptyRow()] };
}

export function buildAction(step) {
	if (step.type === KEPT) return step.raw;
	const extras = omitKeys(step.raw, OWNED[step.type] || ['type']);
	const values = rowsToObject(step.rows);
	if (step.type === SET_VALUES) {
		// Sent even when empty: the API's "needs at least one value" must surface,
		// never a step that vanishes while its card is still on screen.
		return { ...extras, type: SET_VALUES, elements: values };
	}
	const action = { ...extras, type: CREATE_ITEM, card_id: step.cardId };
	if (Object.keys(values).length) action.elements = values;
	if (step.linkBackElement) action.linkBackElement = step.linkBackElement;
	const on = (step.dedupeOn || []).filter(Boolean);
	if (on.length) action.dedupe = { ...omitKeys(step.raw?.dedupe, ['on']), on };
	return action;
}

/** Does any value of this step read `{{input.<key>}}`? (A removed input would break it.) */
export function stepReadsInput(step, key) {
	const needle = new RegExp(`\\{\\{\\s*input\\.${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(?:[+-]\\s*\\d+\\s*[dwm])?\\s*\\}\\}`);
	const rows = Array.isArray(step.rows) ? step.rows : [];
	if (rows.some(row => needle.test(Array.isArray(row.value) ? row.value.join(',') : String(row.value ?? '')))) return true;
	return step.type === KEPT && needle.test(JSON.stringify(step.raw || {}));
}

export const inputIsRead = (steps, key) => (steps || []).some(step => stepReadsInput(step, key));

// -------------------------------------------------------------------- inputs

export function inputFromStored(input) {
	return {
		uid: nextKey('i'),
		key: input.key,
		label: input.label || '',
		dataType: input.dataType,
		options: input.options || {},
		// A saved input keeps its key: steps and presses name it.
		saved: true,
	};
}

export function newInput(taken) {
	return { uid: nextKey('i'), key: uniqueKey('input', taken), label: '', dataType: 'text-small', options: {}, saved: false, auto: true };
}

/**
 * A new input's key follows its label ("Hours spent" is `hours-spent`) while
 * nothing could be holding the old one: it is not saved and no step reads it.
 * Once a step reads it the key is the step's, and stays. Returns the input
 * with its key (and `auto`) settled.
 */
export function withKeyFollowingLabel(prev, next, { steps, taken }) {
	if (prev.saved || !prev.auto) return next;
	if (inputIsRead(steps, prev.key)) return { ...next, key: prev.key, auto: false };
	if (next.label === prev.label) return next;
	const slug = slugify(next.label, '');
	if (!slug) return next;
	return { ...next, key: uniqueKey(slug, new Set([...taken].filter(key => key !== prev.key))) };
}

export const buildInput = input => ({
	key: input.key,
	label: String(input.label || '').trim(),
	dataType: input.dataType,
	options: input.options || {},
});

// ---------------------------------------------------------------- whole draft

/** The editor's state from a stored action (null: a new one). */
export function draftFromRule(rule) {
	const trigger = rule?.trigger || {};
	const button = rule?.button || {};
	const name = rule?.name || '';
	return {
		name,
		cardId: normalizeId(trigger.card_id) || '',
		kql: trigger.condition?.kql || '',
		// A label that is only the name is the default: renaming then renames the button.
		label: button.label && button.label !== name ? button.label : '',
		icon: button.icon || '',
		confirm: button.confirm || '',
		reason: button.reason || '',
		inputs: (Array.isArray(rule?.inputs) ? rule.inputs : []).map(inputFromStored),
		steps: (Array.isArray(rule?.actions) ? rule.actions : []).map(stepFromAction),
	};
}

export function blankDraft() {
	return { name: '', cardId: '', kql: '', label: '', icon: '', confirm: '', reason: '', inputs: [], steps: [newStep(SET_VALUES)] };
}

/**
 * The body of POST (a new action) or PATCH (`merge: false`, a whole-action
 * editor: a key left out is a setting the manager cleared). `kind` is sent on
 * a create only.
 */
export function buildPayload(draft, { isNew }) {
	const name = draft.name.trim();
	const trigger = { type: 'manual', card_id: draft.cardId };
	if (draft.kql.trim()) trigger.condition = { kql: draft.kql.trim() };
	const button = { label: draft.label.trim() || name };
	if (draft.icon) button.icon = draft.icon;
	if (draft.confirm.trim()) button.confirm = draft.confirm.trim();
	if (draft.reason.trim()) button.reason = draft.reason.trim();
	const payload = {
		...(isNew ? { kind: 'card-action' } : {}),
		name,
		trigger,
		button,
		inputs: draft.inputs.map(buildInput),
		actions: draft.steps.map(buildAction).filter(Boolean),
	};
	return isNew ? payload : { ...payload, merge: false };
}

// ------------------------------------------------------------------- refusals

/**
 * Where an API refusal belongs on the editor, from its body: `{ where, input?,
 * step?, message }`. `where` is one of `name`, `card`, `label`, `icon`,
 * `confirm`, `reason`, `condition`, `input` (with its `key`), `step` (with its
 * `index`), `steps` (the section as a whole) or `general`. The message is the
 * API's own, verbatim.
 */
export function refusalTarget(data) {
	const message = String(data?.message || '').trim();
	const code = data?.code || '';
	if (code === 'invalid_button') {
		const field = ['label', 'icon', 'confirm', 'reason'].includes(data?.field) ? data.field : 'label';
		return { where: field, message };
	}
	if (['invalid_condition', 'unknown_element', 'ambiguous_element'].includes(code) || /^trigger\.condition/.test(message)) {
		return { where: 'condition', message };
	}
	if (code === 'action_not_allowed' && Number.isInteger(data?.index)) return { where: 'step', index: data.index, message };
	const stepMatch = message.match(/^actions\[(\d+)\]/);
	if (stepMatch) return { where: 'step', index: Number(stepMatch[1]), message };
	const inputMatch = message.match(/^inputs\.([a-z0-9-]+)\b/);
	if (inputMatch) return { where: 'input', key: inputMatch[1], message };
	if (code === 'input_required' || code === 'invalid_input') return { where: 'inputs', message };
	if (code === 'unknown_input' || code === 'input_not_available' || code === 'lookup_source_refused') return { where: 'steps', message };
	if (/^button\.(label|icon|confirm|reason)/.test(message)) return { where: message.match(/^button\.(\w+)/)[1], message };
	if (/^name\b/i.test(message)) return { where: 'name', message };
	if (/^trigger\.card_id|^trigger\b.*\bcard\b/i.test(message)) return { where: 'card', message };
	if (/^A card action needs at least one action/.test(message)) return { where: 'steps', message };
	return { where: 'general', message: message || 'Could not save the action.' };
}

// --------------------------------------------------------------------- words

const joinAnd = words => (words.length <= 1 ? words[0] || '' : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * One action's row on the Automations tab, in a sentence. Reads the stored
 * shape, never the draft: an action made through the API summarizes as well.
 */
export function describeCardAction(rule, cardsById = {}) {
	const cardName = cardsById?.[normalizeId(rule?.trigger?.card_id)]?.name;
	const label = rule?.button?.label || rule?.name || 'the button';
	const where = cardName ? `a ${cardName}` : 'a record';
	let text = `Button “${label}” on ${where}`;
	if (rule?.trigger?.condition?.kql) text += ', only when its condition holds';
	const inputs = Array.isArray(rule?.inputs) ? rule.inputs.length : 0;
	if (inputs) text += `, asks for ${plural(inputs, 'value', 'values')}`;
	const kinds = (Array.isArray(rule?.actions) ? rule.actions : []).map(action => {
		if (action?.type === SET_VALUES) return 'sets values';
		if (action?.type === CREATE_ITEM) {
			const created = cardsById?.[normalizeId(action.card_id)]?.name;
			return created ? `creates a ${created}` : 'creates an item';
		}
		if (action?.type === 'update-items') return 'updates related items';
		return null;
	}).filter(Boolean);
	const unique = [...new Set(kinds)];
	if (unique.length) text += ` — ${joinAnd(unique)}`;
	return text;
}

/**
 * A condition that names elements the card no longer has, in a sentence: the
 * stored ids are all that is left of them, so they are named by those.
 */
export function goneElementSentence(entries, cardName) {
	const ids = (entries || []).map(entry => `#${String(entry.id).replace(/^#/, '')}`);
	const where = cardName || 'this card';
	if (ids.length === 1) return `An element this condition uses (${ids[0]}) is no longer on ${where}, so the button cannot run. Remove that part of the condition, or pick another element.`;
	return `${ids.length} elements this condition uses (${ids.join(', ')}) are no longer on ${where}, so the button cannot run. Remove those parts of the condition, or pick other elements.`;
}

/** A stored step this editor keeps as it is, in a sentence. */
export function describeKeptStep(raw, cardsById = {}) {
	if (raw?.type === 'update-items') return 'Updates related items. It was set up outside this editor and is kept as it is.';
	return 'This step was set up outside this editor and is kept as it is.';
}

// ------------------------------------------------------------------- preview

/**
 * A value the preview reports, in words. The API has rendered and typed it as
 * the press would write it; this only reads it aloud. A link is a record id,
 * which cannot be named here: it says so, or says it is the record pressed on.
 */
export function previewValueText(value, element, { pressedId = '', pressedName = '' } = {}) {
	if (value === null || value === undefined || value === '') return 'Empty';
	const HEX = /^[0-9a-f]{24}$/i;
	const lookup = element?.dataType === 'card-lookup';
	const list = Array.isArray(value) ? value : null;
	if (lookup || (list && list.length && list.every(v => HEX.test(String(v))))) {
		const ids = (list || [value]).map(v => String(v));
		if (pressedId && ids.length === 1 && ids[0] === pressedId) return pressedName ? `This ${pressedName}` : 'The record pressed on';
		return ids.length === 1 ? 'A linked record' : `${ids.length} linked records`;
	}
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (isMeasurementValue(value)) return formatMeasurementValue(value, element?.options);
	if (isCurrencyValue(value)) return formatCurrencyValue(value, element?.options);
	if (list) return list.map(v => previewValueText(v, null)).join(', ');
	if (typeof value === 'object') return `${Object.values(value).join(' ')}`;
	return String(value);
}
